/*
 * Copyright 2022 Ardalan Amini
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { NextPage } from "next";
import Head from "next/head";
import Image from "next/image";

const Home: NextPage = () => {
  return (
    <div className="bg-white text-black dark:bg-black dark:text-white py-0 px-8">
      <Head>
        <title>Ardalan Amini</title>
        <meta name="description" content="Ardalan Amini Personal Portfolio"/>
        <link rel="icon" href="/favicon.ico"/>
      </Head>

      <main className="min-h-screen py-16 px-0 flex-1 flex flex-col justify-center items-center">
        <h1 className="font-bold m-0 text-[4rem] leading-[1.15] text-center">
          Welcome to{" "}
          <a
            href="https://nextjs.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0070f3] no-underline hover:underline focus:underline active:underline"
          >
            Next.js!
          </a>
        </h1>

        <p className="my-16 mx-0 text-2xl leading-[1.5rem] text-center">
          Get started by editing{" "}
          <code
            className="bg-[#fafafa] dark:bg-[#252525] rounded-[5px] p-3 text-[1.1rem] font-mono">src/pages/index.tsx</code>
        </p>

        <div className="flex items-center justify-center flex-wrap max-w-[800px] flex-col w-full sm:flex-row">
          <a
            href="https://nextjs.org/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="m-4 p-6 text-left text-inherit no-underline border border-solid border-[#eaeaea] dark:border-[#252525] rounded-[10px] transition-colors max-w-[300px] hover:text-[#0070f3] focus:text-[#0070f3] active:text-[#0070f3] hover:border-[#0070f3] focus:border-[#0070f3] active:border-[#0070f3]"
          >
            <h2 className="font-bold m-0 mb-4 text-2xl">Documentation &rarr;</h2>
            <p className="m-0 text-xl leading-normal">Find in-depth information about Next.js features and API.</p>
          </a>

          <a
            href="https://nextjs.org/learn"
            target="_blank"
            rel="noopener noreferrer"
            className="m-4 p-6 text-left text-inherit no-underline border border-solid border-[#eaeaea] dark:border-[#252525] rounded-[10px] transition-colors max-w-[300px] hover:text-[#0070f3] focus:text-[#0070f3] active:text-[#0070f3] hover:border-[#0070f3] focus:border-[#0070f3] active:border-[#0070f3]"
          >
            <h2 className="font-bold m-0 mb-4 text-2xl">Learn &rarr;</h2>
            <p className="m-0 text-xl leading-normal">Learn about Next.js in an interactive course with quizzes!</p>
          </a>

          <a
            href="https://github.com/vercel/next.js/tree/canary/examples"
            target="_blank"
            rel="noopener noreferrer"
            className="m-4 p-6 text-left text-inherit no-underline border border-solid border-[#eaeaea] dark:border-[#252525] rounded-[10px] transition-colors max-w-[300px] hover:text-[#0070f3] focus:text-[#0070f3] active:text-[#0070f3] hover:border-[#0070f3] focus:border-[#0070f3] active:border-[#0070f3]"
          >
            <h2 className="font-bold m-0 mb-4 text-2xl">Examples &rarr;</h2>
            <p className="m-0 text-xl leading-normal">Discover and deploy boilerplate example Next.js projects.</p>
          </a>

          <a
            href="https://vercel.com/new?utm_source=create-next-app&utm_medium=default-template&utm_campaign=create-next-app"
            target="_blank"
            rel="noopener noreferrer"
            className="m-4 p-6 text-left text-inherit no-underline border border-solid border-[#eaeaea] dark:border-[#252525] rounded-[10px] transition-colors max-w-[300px] hover:text-[#0070f3] focus:text-[#0070f3] active:text-[#0070f3] hover:border-[#0070f3] focus:border-[#0070f3] active:border-[#0070f3]"
          >
            <h2 className="font-bold m-0 mb-4 text-2xl">Deploy &rarr;</h2>
            <p className="m-0 text-xl leading-normal">
              Instantly deploy your Next.js site to a public URL with Vercel.
            </p>
          </a>
        </div>
      </main>

      <footer
        className="flex flex-1 py-8 px-0 border-t border-solid border-[#eaeaea] dark:border-[#252525] justify-center items-center">
        <a
          href="https://vercel.com?utm_source=create-next-app&utm_medium=default-template&utm_campaign=create-next-app"
          target="_blank"
          rel="noopener noreferrer"
          className="flex justify-center items-center grow"
        >
          Powered by{" "}
          <span className="h-[1em] ml-2">
            {
              typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
                ? <Image src="/vercel-light.svg" alt="Vercel Logo" width={72} height={16}/>
                : <Image src="/vercel.svg" alt="Vercel Logo" width={72} height={16}/>
            }
          </span>
        </a>
      </footer>
    </div>
  );
};

export default Home;
